
import Vue from 'vue';
import Component from 'vue-class-component';
import {namespace} from 'vuex-class';
import {IOnOffHandlers} from 'bonomo/dist';
import {IInvoice, IInvoiceOrder} from '@/model/Invoice/invoiceInterfaces';
import Datim from '@/components/shared/Datim.vue';

const invoiceModule = namespace('invoiceModule');

@Component({
  components: {Datim},
})
export default class InvoiceCreate extends Vue {
  @invoiceModule.Getter('ordersForInvoicing')
  protected ordersForInvoicing!: IInvoiceOrder[];
  @invoiceModule.Getter('invoice')
  protected invoice!: IInvoice;

  protected loading: boolean = true;
  protected submitting: boolean = false;
  protected selectedOrders: IInvoiceOrder[] = [];
  protected draggedOrder: IInvoiceOrder | null = null;
  protected draggedFrom: string = '';
  protected dragOverTarget: string = '';
  private subscribes: IOnOffHandlers = {};

  get availableOrders(): IInvoiceOrder[] {
    const selectedIds = this.selectedOrders.map((o: IInvoiceOrder) => o.id);
    return this.ordersForInvoicing.filter((o: IInvoiceOrder) => selectedIds.indexOf(o.id) === -1);
  }

  created() {
    this.subscribes = (this as any).$ons({
      'backend.call.OK.ordersForInvoicingLoaded': this.onOrdersLoaded,
      'backend.call.OK.invoiceLoaded': this.onInvoiceCreated,
      'backend.call.KO': this.onError,
    });
    this.$store.dispatch('invoiceModule/fetchOrdersForInvoicing');
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  protected orderStatusColor(status: string): string {
    if (status === 'status_order_completed') return 'green';
    if (status === 'status_order_client_cancelled' || status === 'status_order_sp_cancelled' || status === 'status_order_sp_rejected') return 'red';
    if (status === 'status_order_in_progress') return 'blue';
    return 'blue-grey';
  }

  protected onCreate() {
    this.submitting = true;
    const orderIds = this.selectedOrders.map((o: IInvoiceOrder) => o.id);
    this.$store.dispatch('invoiceModule/createInvoice', orderIds);
  }

  protected addToSelected(order: IInvoiceOrder) {
    this.selectedOrders = [...this.selectedOrders, order];
  }

  protected removeFromSelected(order: IInvoiceOrder) {
    this.selectedOrders = this.selectedOrders.filter((o: IInvoiceOrder) => o.id !== order.id);
  }

  protected onDragStart(order: IInvoiceOrder, from: string) {
    this.draggedOrder = order;
    this.draggedFrom = from;
  }

  protected onDragOver(e: DragEvent, target: string) {
    e.preventDefault();
    this.dragOverTarget = target;
  }

  protected onDragLeave(e: DragEvent) {
    const related = e.relatedTarget as Node | null;
    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
    this.dragOverTarget = '';
  }

  protected onDrop(e: DragEvent, target: string) {
    e.preventDefault();
    this.dragOverTarget = '';
    if (!this.draggedOrder || this.draggedFrom === target) {
      this.draggedOrder = null;
      this.draggedFrom = '';
      return;
    }
    if (target === 'selected') {
      this.selectedOrders = [...this.selectedOrders, this.draggedOrder];
    } else {
      const dropped = this.draggedOrder;
      this.selectedOrders = this.selectedOrders.filter((o: IInvoiceOrder) => o.id !== dropped.id);
    }
    this.draggedOrder = null;
    this.draggedFrom = '';
  }

  protected onDragEnd() {
    this.draggedOrder = null;
    this.draggedFrom = '';
    this.dragOverTarget = '';
  }

  private onOrdersLoaded() {
    this.loading = false;
  }

  private onInvoiceCreated() {
    this.$router.push({name: 'invoice', params: {invoiceId: String(this.invoice.id)}});
  }

  private onError() {
    this.submitting = false;
  }
}
